/*
 * Copyright (c) 2018 teamapps.org (see code comments for author's name)
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */


import {TeamAppsUiContext} from "./TeamAppsUiContext";
import * as log from "loglevel";
import {UiComponentConfig} from "../generated/UiComponentConfig";

/**
 * @author Yann Massard (yamass@gmail.com)
 */
export abstract class UiComponent<C extends UiComponentConfig = UiComponentConfig> {

	protected logger: log.Logger = log.getLogger((<any>(this.constructor)).name || this.constructor.toString().match(/\w+/g)[1]);

	private _attachedToDom = false;
	private width: number = 0;
	private height: number = 0;
	private firstReLayout = true;

	constructor(protected _config: C,
	            protected _context: TeamAppsUiContext) {
	}

	public getId(): string {
		return this._config.id;
	}

	public getTeamAppsType(): string {
		return this._config._type;
	}

	get attachedToDom() {
		return this._attachedToDom;
	}

	set attachedToDom(attachedToDom) {
		let wasAttachedToDom = this._attachedToDom;
		this._attachedToDom = attachedToDom;
		if (attachedToDom && !wasAttachedToDom) {
			this.width = this.getMainDomElement()[0].offsetWidth;
			this.height = this.getMainDomElement()[0].offsetHeight;
			this.onAttachedToDom();
			this.reLayout();
		}
	}

	/**
	 * This method should get called from a container component when the available size for a child changes.
	 */
	public reLayout(force?: boolean): void {
		if (this.attachedToDom) {
			let availableWidth = this.getMainDomElement()[0].offsetWidth;
			let availableHeight = this.getMainDomElement()[0].offsetHeight;
			if (this.firstReLayout || force || availableWidth !== this.width || availableHeight !== this.height) {
				this.logger.trace("resize: " + this.getId());
				this.firstReLayout = false;
				this.width = availableWidth;
				this.height = availableHeight;
				this.onResize();
			}
		}
	}

	/**
	 * This method gets called whenever the available size for this component changes.
	 */
	public onResize(): void {
		// do nothing (default implementation)
	}

	/**
	 * This method is called when the component is removed (implicitely or explicitely) from the component tree.
	 * It should be used to release any resources the component holds. This can be:
	 *   - additional DOM elements that are not children of the component's main DOM element
	 *   - other resources that are not released just by removing the component's main DOM element
	 *
	 * This method gets called BEFORE the component gets detached from the DOM.
	 * If a parent detaches a child component, this method MUST be called on the child component BEFORE the child gets detached from the DOM.
	 *
	 * Components with children MUST call this method on their children inside their implementation of this method.
	 */
	public abstract destroy(): void;

	/**
	 * @return The main DOM element of this component.
	 * This method is used by the main TeamApps UI mechanism to get the element and attach it to the DOM.
	 */
	public abstract getMainDomElement(): JQuery;

	/**
	 * Override this method to execute code that cannot be executed before the field is attached to the DOM.
	 *
	 * Example 1:
	 *   An HTML/Flash video player with autoplay = true will not be able to play until it is attached to the DOM.
	 *   So the code for starting the video will need to be executed in this (overwritten) method.
	 * Example 2:
	 *   UiTable uses slickgrid. slickgrid can only initialize correctly when attached to the DOM.
	 *
	 * Components containing other components should set attachedToDom on their child components.
	 * This will in turn invoke this method on the children, so the call propagates recursively.
	 *
	 * Example:
	 *   A component with child components will call "child.attachedToDom = true" on its children.
	 */
	protected onAttachedToDom(): void {
		// do nothing (default implementation)
	}

	public getWidth(): number {
		return this.width;
	}

	public getHeight(): number {
		return this.height;
	}
}